import videojs from 'video.js'
import { getTypeFromSource, registerPluginOnce } from '../utils.js'
import { CustomEvents } from '../types/index.ts'

const Plugin = videojs.getPlugin('plugin')

const REFRESH_BUFFER_MS = 60 * 1000 // 60 seconds
const EXPIRES_FRAGMENT_PREFIX = '#expires='

/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 * 
 * Sends custom event if source url is expired, based on the presence of a fragment
 * #expires=ExpirationDateInMilliseconds
 */
class NFBExpiredSourceWarning extends Plugin {
  /**
   * Create a NFBExpiredSourceWarning plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
  constructor (player, options) {
    // the parent class will add player under this.player
    super(player)
    this.log.level(videojs.log.level())
    this.player.on('play', this.verifySourceIsExpired)
  }

  verifySourceIsExpired = () => {
    if (this.sourceIsExpired(this.player.src())) {
      this.dispatchExpiredSourceWarningEvent()
    }
  }

  sourceIsExpired = (sourceUrl) => {
    // Currently only for HLS
    if (getTypeFromSource(sourceUrl) !== 'hls') {
      return false
    }

    const expirationDate = this.getExpiresDateFromSourceUrl(sourceUrl)

    if (!expirationDate) {
      // Always considered not expired if no expiration date
      return false
    }
  
    return expirationDate < new Date(Date.now() + REFRESH_BUFFER_MS)
  }

  getExpiresDateFromSourceUrl = (sourceUrl) => {
    const expiresFragment = (new URL(sourceUrl)).hash

    if (!expiresFragment.includes(EXPIRES_FRAGMENT_PREFIX)) {
      return null
    }

    return new Date(parseInt(expiresFragment.replace(EXPIRES_FRAGMENT_PREFIX, '')) * 1000)
  }

  dispatchExpiredSourceWarningEvent = () => {
    this.player.el().dispatchEvent(new CustomEvent(CustomEvents.sourceHasExpired, {
      composed: true,
      bubbles: true,
    }))
  }
}

// Define default values for the plugin's `state` object here.
NFBExpiredSourceWarning.defaultState = {}

// Include the version number.
NFBExpiredSourceWarning.VERSION = '1.0.0'

// Register the plugin
registerPluginOnce(videojs, 'nfbExpiredSourceWarning', NFBExpiredSourceWarning)

export default NFBExpiredSourceWarning
